import { queryOptions } from '@tanstack/react-query';

import { api } from '../client';
import { endpoints } from '../endpoints';
import type {
  Achievement,
  Paginated,
  PlayerPoints,
  PlayerProfile,
  PlayerSummary,
  SpeedrunSubmission,
  Viewer,
} from '../types';
import {
  achievement as normalizeAchievement,
  paginated,
  playerPoints,
  playerProfile,
  playerSummary,
  post as normalizePost,
  run as normalizeRun,
  viewer as normalizeViewer,
  type WireAchievement,
  type WirePlayerSummary,
  type WirePointsSummary,
  type WirePost,
  type WireRun,
} from '../wire';

export interface FollowRow extends PlayerSummary {
  points: number;
  isFollowing: boolean | null;
}

const followRow = (raw: WirePlayerSummary): FollowRow => ({
  ...playerSummary(raw),
  points: typeof raw.points === 'number' ? raw.points : 0,
  isFollowing: raw.is_following ?? null,
});

export const playerQueries = {
  profile: (publicPlayerId: string) =>
    queryOptions({
      queryKey: ['players', publicPlayerId, 'profile'] as const,
      queryFn: async (): Promise<PlayerProfile> =>
        playerProfile(await api.get<WirePlayerSummary>(endpoints.players.profile(publicPlayerId))),
      enabled: publicPlayerId.length > 0,
    }),

  achievements: (publicPlayerId: string, page = 1) =>
    queryOptions({
      queryKey: ['players', publicPlayerId, 'achievements', page] as const,
      queryFn: async (): Promise<Paginated<Achievement>> => {
        const { data, meta } = await api.getEnvelope<{ items?: WireAchievement[] }>(
          endpoints.players.achievements(publicPlayerId),
          { query: { page, per_page: 40 } },
        );
        return paginated(data, meta, normalizeAchievement);
      },
      enabled: publicPlayerId.length > 0,
    }),

  speedruns: (publicPlayerId: string, page = 1) =>
    queryOptions({
      queryKey: ['players', publicPlayerId, 'speedruns', page] as const,
      queryFn: async (): Promise<Paginated<SpeedrunSubmission>> => {
        const { data, meta } = await api.getEnvelope<{ items?: WireRun[] }>(
          endpoints.players.speedruns(publicPlayerId),
          { query: { page } },
        );
        return paginated(data, meta, normalizeRun);
      },
      enabled: publicPlayerId.length > 0,
    }),

  points: (publicPlayerId: string) =>
    queryOptions({
      queryKey: ['players', publicPlayerId, 'points'] as const,
      queryFn: async (): Promise<PlayerPoints> =>
        playerPoints(await api.get<WirePointsSummary>(endpoints.players.points(publicPlayerId))),
      enabled: publicPlayerId.length > 0,
    }),

  posts: (publicPlayerId: string, page = 1) =>
    queryOptions({
      queryKey: ['players', publicPlayerId, 'posts', page] as const,
      queryFn: async () => {
        const { data, meta } = await api.getEnvelope<{ items?: WirePost[] }>(
          endpoints.players.posts(publicPlayerId),
          { query: { page } },
        );
        return paginated(data, meta, normalizePost);
      },
      enabled: publicPlayerId.length > 0,
    }),

  followList: (kind: 'followers' | 'following', publicPlayerId: string, page = 1) =>
    queryOptions({
      queryKey: ['players', publicPlayerId, 'follow-list', kind, page] as const,
      queryFn: async () => {
        const { data, meta } = await api.getEnvelope<{ items?: WirePlayerSummary[] }>(
          kind === 'followers'
            ? endpoints.players.followers(publicPlayerId)
            : endpoints.players.following(publicPlayerId),
          { query: { page } },
        );
        return paginated(data, meta, followRow);
      },
      enabled: publicPlayerId.length > 0,
    }),

  search: (term: string, page = 1, extras: { country?: string; role?: string; sort?: string } = {}) =>
    queryOptions({
      queryKey: ['players', 'search', term, page, extras.country ?? '', extras.role ?? '', extras.sort ?? ''] as const,
      queryFn: async () => {
        const { data, meta } = await api.getEnvelope<{ items?: WirePlayerSummary[] }>(endpoints.players.search(), {
          query: { q: term, page, country: extras.country, role: extras.role, sort: extras.sort },
        });
        return paginated(data, meta, followRow);
      },
    }),
};

export const playerApi = {
  async setFollowing(publicPlayerId: string, following: boolean) {
    const path = endpoints.players.follow(publicPlayerId);
    if (following) await api.post(path);
    else await api.delete(path);
    return { isFollowing: following };
  },

  async updateProfile(input: {
    nickname?: string;
    bio?: string | null;
    countryCode?: string | null;
    locale?: 'zh' | 'en';
    gender?: 'male' | 'female' | 'other' | 'prefer_not_to_say' | null;
    birthday?: string | null;
    socialLinks?: string[] | null;
  }): Promise<Viewer> {
    const body: Record<string, unknown> = {};
    if (input.nickname !== undefined) body.nickname = input.nickname;
    if (input.bio !== undefined) body.bio = input.bio ?? '';
    if (input.countryCode !== undefined) body.country_code = input.countryCode ?? '';
    if (input.locale !== undefined) body.locale = input.locale;
    if (input.gender !== undefined) body.gender = input.gender;
    if (input.birthday !== undefined) body.birthday = input.birthday ?? '';
    if (input.socialLinks !== undefined) body.social_links = input.socialLinks ?? [];
    return normalizeViewer(await api.patch<import('../wire').WirePlayerSelf>(endpoints.account.profile(), body));
  },

  async updateAvatar(file: File): Promise<Viewer> {
    const form = new FormData();
    form.append('avatar', file);
    return normalizeViewer(await api.post<import('../wire').WirePlayerSelf>(endpoints.account.avatar(), form));
  },

  async deleteAvatar(): Promise<Viewer> {
    return normalizeViewer(await api.delete<import('../wire').WirePlayerSelf>(endpoints.account.avatar()));
  },
};
